/**
 * Enhanced API Service with Centralized Configuration and Interceptors
 * This service provides a centralized way to handle all API requests with proper error handling,
 * authentication, and request/response interceptors.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { getApiConfig, getCurrentEnvConfig } from "../config/api";
import { tokenStorage } from "./tokenStorage";
import {
  ApiResponse,
  AuthResponse,
  RefreshResponse,
  DashboardStats,
  GrowthAnalytics,
  PlatformAnalytics,
  SystemHealth,
  User,
  PaginatedResponse,
  AdoptionHistoryResponse,
  AdoptionHistoryParams,
} from "../types";

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      startTime: Date;
    };
  }
}

/**
 * API Interceptor Manager
 * Handles request/response interceptors and token refresh logic
 */
class ApiInterceptorManager {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor(private apiInstance: AxiosInstance, private config: ReturnType<typeof getApiConfig>) {}

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Setup request interceptor
   */
  setupRequestInterceptor() {
    this.apiInstance.interceptors.request.use(
      (config) => {
        // Add auth token to requests
        const token = tokenStorage.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Setup response interceptor
   */
  setupResponseInterceptor() {
    this.apiInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response time for debugging
        if (response.config.metadata?.startTime) {
          const endTime = new Date();
          const duration = endTime.getTime() - response.config.metadata.startTime.getTime();
          console.log(`API Request to ${response.config.url} took ${duration}ms`);
        }
        
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle 401 errors and token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.apiInstance(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = tokenStorage.getRefreshToken();
          
          if (!refreshToken) {
            // No refresh token available, redirect to login
            tokenStorage.clearTokens();
            this.processQueue(error, null);
            window.location.href = "/login";
            return Promise.reject(error);
          }

          try {
            // Attempt to refresh the token
            const response = await axios.post<RefreshResponse>(
              `${this.config.baseURL}${this.config.endpoints.auth.refresh}`,
              { refreshToken }
            );

            if (response.data.success) {
              const { accessToken } = response.data;
              
              // Update the stored access token
              tokenStorage.updateAccessToken(accessToken);
              
              // Update the original request with new token
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              
              // Process queued requests
              this.processQueue(null, accessToken);
              
              // Retry the original request
              return this.apiInstance(originalRequest);
            } else {
              throw new Error('Refresh failed');
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            tokenStorage.clearTokens();
            this.processQueue(refreshError, null);
            window.location.href = "/login";
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Initialize all interceptors
   */
  initialize() {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }
}

/**
 * Enhanced API Service Class
 */
class ApiService {
  private apiInstance: AxiosInstance;
  private interceptorManager: ApiInterceptorManager;
  private config: ReturnType<typeof getApiConfig>;

  constructor() {
    this.config = getApiConfig();
    const envConfig = getCurrentEnvConfig();
    
    this.apiInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: envConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.interceptorManager = new ApiInterceptorManager(this.apiInstance, this.config);
    this.interceptorManager.initialize();
  }

  /**
   * Get the axios instance for direct use if needed
   */
  getInstance(): AxiosInstance {
    return this.apiInstance;
  }

  /**
   * Authentication methods
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.apiInstance.post<AuthResponse>(
      this.config.endpoints.auth.login,
      { email, password }
    );
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<RefreshResponse> {
    const response = await this.apiInstance.post<RefreshResponse>(
      this.config.endpoints.auth.refresh,
      { refreshToken }
    );
    return response.data;
  }

  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await this.apiInstance.post(this.config.endpoints.auth.logout, { refreshToken });
      } catch (error) {
        console.error('Logout request failed:', error);
      }
    }
    tokenStorage.clearTokens();
  }

  /**
   * Dashboard methods
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.apiInstance.get<ApiResponse<DashboardStats>>(
      this.config.endpoints.admin.dashboard
    );
    return response.data.data;
  }

  /**
   * Analytics methods
   */
  async getGrowthAnalytics(period: string = "30d"): Promise<GrowthAnalytics> {
    const response = await this.apiInstance.get<ApiResponse<GrowthAnalytics>>(
      `${this.config.endpoints.admin.analytics.growth}?period=${period}`
    );
    return response.data.data;
  }

  async getPlatformAnalytics(): Promise<PlatformAnalytics> {
    const response = await this.apiInstance.get<ApiResponse<PlatformAnalytics>>(
      this.config.endpoints.admin.analytics.platform
    );
    return response.data.data;
  }

  async getPetAnalytics(): Promise<any> {
    const response = await this.apiInstance.get<ApiResponse<any>>(
      this.config.endpoints.admin.analytics.pets
    );
    return response.data.data;
  }

  async getMessagingAnalytics(): Promise<any> {
    const response = await this.apiInstance.get<ApiResponse<any>>(
      this.config.endpoints.admin.analytics.messaging
    );
    return response.data.data;
  }

  async getAdoptionHistory(params: AdoptionHistoryParams = {}): Promise<AdoptionHistoryResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.apiInstance.get<ApiResponse<AdoptionHistoryResponse>>(
      `${this.config.endpoints.admin.analytics.adoptionHistory}?${queryParams}`
    );
    return response.data.data;
  }

  /**
   * User management methods
   */
  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    platform?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<PaginatedResponse<User>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.apiInstance.get<ApiResponse<PaginatedResponse<User>>>(
      `${this.config.endpoints.admin.users}?${queryParams}`
    );
    return response.data.data;
  }

  /**
   * System methods
   */
  async getSystemHealth(): Promise<SystemHealth> {
    const response = await this.apiInstance.get<ApiResponse<SystemHealth>>(
      this.config.endpoints.admin.system.health
    );
    return response.data.data;
  }

  /**
   * Generic request method for custom endpoints
   */
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.apiInstance.request<T>(config);
    return response.data;
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export the class for testing purposes
export { ApiService };

// Export the axios instance for backward compatibility
export const api = apiService.getInstance();

// Export adminApi for backward compatibility
export const adminApi = {
  login: apiService.login.bind(apiService),
  refreshToken: apiService.refreshToken.bind(apiService),
  logout: apiService.logout.bind(apiService),
  getDashboardStats: apiService.getDashboardStats.bind(apiService),
  getGrowthAnalytics: apiService.getGrowthAnalytics.bind(apiService),
  getPlatformAnalytics: apiService.getPlatformAnalytics.bind(apiService),
  getUsers: apiService.getUsers.bind(apiService),
  getSystemHealth: apiService.getSystemHealth.bind(apiService),
  getPetAnalytics: apiService.getPetAnalytics.bind(apiService),
  getMessagingAnalytics: apiService.getMessagingAnalytics.bind(apiService),
  getAdoptionHistory: apiService.getAdoptionHistory.bind(apiService),
};

export default apiService;
